import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { ColumnDef, RowSelectionState } from "@tanstack/react-table";
import {
  Eye,
  EyeOff,
  MoreHorizontal,
  FlaskConical,
  PackageOpen,
  RefreshCw,
  Search,
  Tag,
} from "lucide-react";
import { toast } from "sonner";
import {
  ordersApi,
  STATE_LABELS,
  STATE_ORDER,
  type OrderListResponse,
  type OrderListRow,
  type OrderState,
} from "../lib/orders-api";
import { useConfirm } from "@/components/confirm-dialog";
import { EmptyState } from "@/components/empty-state";
import { IndexTable, type IndexTab } from "@/components/index-table";
import { ErrorState } from "@/components/layout";
import { PageHeader } from "@/components/page-header";
import { TestOrderDialog } from "@/components/TestOrderDialog";
import { OrderStateBadge, PrinterStatusBadge, StatusBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useUrlNumber, useUrlParam } from "@/hooks/use-url-state";

type Filter = OrderState | "all" | "hidden";

const PAGE_SIZE = 50;

const plural = (n: number) => `${n} ${n === 1 ? "Bestellung" : "Bestellungen"}`;

export default function Orders() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [data, setData] = useState<OrderListResponse | null>(null);
  const [filterParam, setFilterParam] = useUrlParam("tab", "all");
  const filter = filterParam as Filter;
  const [page, setPage] = useUrlNumber("page", 0);
  const [query, setQuery] = useUrlParam("q", "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selection, setSelection] = useState<RowSelectionState>({});
  const [testOpen, setTestOpen] = useState(false);

  function reload(state: Filter = filter, pageIndex = page) {
    setLoading(true);
    setError(null);
    const paging = { limit: PAGE_SIZE, offset: pageIndex * PAGE_SIZE };
    const opts =
      state === "hidden"
        ? { hidden: true, ...paging }
        : state === "all"
          ? paging
          : { state, ...paging };
    ordersApi
      .list(opts)
      .then((d) => {
        setData(d);
        setSelection({});
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    reload(filter, page);
    // reload ändert sich bei jedem Render; geladen wird bei Filter-/Seitenwechsel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, page]);

  /** Führt eine Aktion für mehrere Bestellungen nacheinander aus (bestehende Einzel-Endpunkte). */
  async function runForEach(
    ids: string[],
    action: (id: string) => Promise<unknown>,
    successText: (n: number) => string,
  ) {
    setLoading(true);
    let failed = 0;
    let lastError = "";
    for (const id of ids) {
      try {
        await action(id);
      } catch (err) {
        failed++;
        lastError = (err as Error).message;
      }
    }
    const ok = ids.length - failed;
    if (ok > 0) toast.success(successText(ok));
    if (failed > 0)
      toast.error(`${plural(failed)} fehlgeschlagen: ${lastError}`);
    reload();
  }

  async function setHidden(ids: string[], hidden: boolean) {
    await runForEach(
      ids,
      (id) => ordersApi.setHidden(id, hidden),
      (n) =>
        hidden ? `${plural(n)} versteckt` : `${plural(n)} wieder eingeblendet`,
    );
  }

  async function setState(ids: string[], state: OrderState) {
    const ok = await confirm({
      title: `Status auf „${STATE_LABELS[state]}“ setzen?`,
      description:
        `Betrifft ${plural(ids.length)}.\n\n` +
        "Es wird nur der Status geändert – es werden keine weiteren Schritte ausgelöst " +
        "(kein Upload, keine Generierung, keine Mail).",
      confirmLabel: "Status setzen",
    });
    if (!ok) return;
    await runForEach(
      ids,
      (id) => ordersApi.setState(id, state),
      (n) => `Status bei ${plural(n)} auf „${STATE_LABELS[state]}“ gesetzt`,
    );
  }

  const countsMap = useMemo(() => {
    const m = new Map<OrderState, number>();
    for (const row of data?.counts_by_state ?? []) m.set(row.state, row.count);
    return m;
  }, [data]);

  const totalCount = useMemo(
    () => Array.from(countsMap.values()).reduce((a, b) => a + b, 0),
    [countsMap],
  );

  // Gesamtzahl für den aktiven Filter – daraus ergibt sich die Seitenzahl.
  const filterTotal =
    filter === "all"
      ? totalCount
      : filter === "hidden"
        ? (data?.hidden_count ?? 0)
        : (countsMap.get(filter) ?? 0);
  const pageCount = Math.max(1, Math.ceil(filterTotal / PAGE_SIZE));

  // Wird die letzte Seite durch Verstecken/Statuswechsel leer, eine Seite zurück.
  useEffect(() => {
    if (data && page > pageCount - 1) setPage(pageCount - 1);
  }, [data, page, pageCount, setPage]);

  const visibleOrders = useMemo(() => {
    const rows = data?.orders ?? [];
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((o) =>
      [
        o.order_number,
        o.shopify_order_number,
        o.shopify_order_id,
        o.customer_email,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [data, query]);

  const tabs: IndexTab[] = [
    { value: "all", label: "Alle", count: totalCount },
    ...STATE_ORDER.filter(
      (s) => (countsMap.get(s) ?? 0) > 0 || filter === s,
    ).map((s) => ({
      value: s,
      label: STATE_LABELS[s],
      count: countsMap.get(s) ?? 0,
    })),
    { value: "hidden", label: "Versteckt", count: data?.hidden_count ?? 0 },
  ];

  const columns: ColumnDef<OrderListRow, unknown>[] = [
    {
      id: "order",
      header: "Bestellung",
      cell: ({ row }) => {
        const o = row.original;
        return (
          <div className="leading-tight">
            <Link
              to={`/orders/${o.id}`}
              className="font-semibold hover:underline focus-visible:underline"
            >
              {o.order_number}
            </Link>
            <div className="text-xs text-muted-foreground">
              Shopify {o.shopify_order_number ?? o.shopify_order_id ?? "—"}
            </div>
          </div>
        );
      },
    },
    {
      id: "state",
      header: "Status",
      cell: ({ row }) => (
        <div className="flex flex-wrap items-center gap-1">
          <OrderStateBadge state={row.original.state} />
          {row.original.has_photo_outfit && (
            <StatusBadge tone="warning" title="Add-on „Outfit vom Foto“">
              Outfit: Foto
            </StatusBadge>
          )}
        </div>
      ),
    },
    {
      id: "printer",
      header: "Druckerei",
      cell: ({ row }) => (
        <PrinterStatusBadge
          mastering={row.original.mastering_status}
          order={row.original.order_status}
        />
      ),
    },
    {
      id: "customer",
      header: "Kunde",
      cell: ({ row }) => (
        <span className="block max-w-56 truncate">
          {row.original.customer_email ?? "—"}
        </span>
      ),
    },
    {
      id: "created_at",
      header: "Eingegangen",
      cell: ({ row }) => (
        <span className="text-muted-foreground">
          {formatDateTime(row.original.created_at)}
        </span>
      ),
    },
    {
      id: "actions",
      header: () => <span className="sr-only">Aktionen</span>,
      meta: { className: "w-10 text-right" },
      cell: ({ row }) => (
        <RowMenu
          order={row.original}
          disabled={loading}
          onSetState={(s) => setState([row.original.id], s)}
          onToggleHidden={() =>
            setHidden([row.original.id], !row.original.hidden)
          }
          onOpen={() => navigate(`/orders/${row.original.id}`)}
        />
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Bestellungen"
        actions={
          <>
            <Button variant="secondary" onClick={() => setTestOpen(true)}>
              <FlaskConical />
              Testbestellung
            </Button>
            <Button
              variant="secondary"
              onClick={() => reload()}
              disabled={loading}
            >
              <RefreshCw className={loading ? "animate-spin" : undefined} />
              Aktualisieren
            </Button>
          </>
        }
      />
      <TestOrderDialog open={testOpen} onOpenChange={setTestOpen} />

      {error && (
        <ErrorState className="mb-3" message={error} onRetry={() => reload()} />
      )}

      <IndexTable
        tabs={tabs}
        tab={filter}
        onTabChange={(v) => setFilterParam(v, { resetPage: true })}
        search={{
          value: query,
          onChange: (v) => setQuery(v),
          placeholder:
            "Auf dieser Seite suchen: Bestellnr., Shopify-Nr., Kunde",
          label: "Bestellungen auf dieser Seite durchsuchen",
        }}
        columns={columns}
        data={visibleOrders}
        getRowId={(o) => o.id}
        rowLabel={(o) => `Bestellung ${o.order_number}`}
        onRowClick={(o) => navigate(`/orders/${o.id}`)}
        loading={!data}
        selection={selection}
        onSelectionChange={setSelection}
        bulkActions={(ids) => (
          <>
            <Button
              size="sm"
              variant="secondary"
              disabled={loading}
              onClick={() => setHidden(ids, filter !== "hidden")}
            >
              {filter === "hidden" ? <Eye /> : <EyeOff />}
              {filter === "hidden" ? "Einblenden" : "Verstecken"}
            </Button>
            <StateMenu disabled={loading} onSelect={(s) => setState(ids, s)}>
              <Button size="sm" variant="secondary" disabled={loading}>
                <Tag />
                Status setzen
              </Button>
            </StateMenu>
          </>
        )}
        pagination={{
          page,
          pageSize: PAGE_SIZE,
          total: filterTotal,
          onPageChange: setPage,
          disabled: loading,
        }}
        empty={
          query.trim() ? (
            <EmptyState
              icon={Search}
              title="Keine Treffer"
              description={`Keine Bestellung auf dieser Seite passt zu „${query.trim()}“.`}
              action={
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setQuery("")}
                >
                  Suche zurücksetzen
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={PackageOpen}
              title={
                filter === "hidden"
                  ? "Keine versteckten Bestellungen"
                  : "Keine Bestellungen in diesem Status"
              }
              action={
                filter !== "all" && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setFilterParam("all", { resetPage: true })}
                  >
                    Alle Bestellungen anzeigen
                  </Button>
                )
              }
            />
          )
        }
      />
    </div>
  );
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString("de-DE", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

/** Dropdown mit allen Status; der Aufrufer bestätigt vor dem Setzen. */
function StateMenu({
  children,
  disabled,
  onSelect,
}: {
  children: React.ReactNode;
  disabled?: boolean;
  onSelect: (s: OrderState) => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        {children}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-80 overflow-y-auto">
        <DropdownMenuLabel>
          Status setzen (ohne Folgeschritte)
        </DropdownMenuLabel>
        {STATE_ORDER.map((s) => (
          <DropdownMenuItem key={s} onSelect={() => onSelect(s)}>
            {STATE_LABELS[s]}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function RowMenu({
  order,
  disabled,
  onSetState,
  onToggleHidden,
  onOpen,
}: {
  order: OrderListRow;
  disabled?: boolean;
  onSetState: (s: OrderState) => void;
  onToggleHidden: () => void;
  onOpen: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={`Aktionen für ${order.order_number}`}
        >
          <MoreHorizontal />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-48">
        <DropdownMenuItem onSelect={onOpen}>Öffnen</DropdownMenuItem>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <Tag />
            Status setzen
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="max-h-80 overflow-y-auto">
            {STATE_ORDER.map((s) => (
              <DropdownMenuItem
                key={s}
                disabled={s === order.state}
                onSelect={() => onSetState(s)}
              >
                {STATE_LABELS[s]}
              </DropdownMenuItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onToggleHidden}>
          {order.hidden ? <Eye /> : <EyeOff />}
          {order.hidden ? "Einblenden" : "Verstecken"}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
